import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isPlanSelectionEligible } from "src/utils/userSegment/features";
import { Serializer } from "@utils/redux/persistence";
import { getSessionState, updateSessionState } from "@pageDefinitions/session";
import { CoreReduxState } from "../internal";
import getStore from "../store";

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    promoCodePersisted?: boolean;
    promoCodePersistedApplied?: string;
  }
}

export type PromoCodeErrorType =
  | "IncorrectPromoCode"
  | "PromoCodeRedemptionBlocked"
  | "UnknownError";

const promoCodeInitialState = {
  promoCodeApplied: "",
  promoDiscountAmount: 0,
  promoDiscountPercentage: 0,
  promoCodeIsVip: false,
  promoCodeError: "",
  promoCodeErrorType: null,
  promoCodeExpirationDate: "",
  promoCodeFreeMonths: null,
} as PromoCodeState;

export type PromoCodeState = {
  promoCodeApplied: string;
  promoDiscountAmount: number;
  promoDiscountPercentage: number;
  promoCodeIsVip: boolean;
  promoCodeError: string;
  promoCodeErrorType: PromoCodeErrorType;
  promoCodeExpirationDate: string;
  promoCodeFreeMonths?: number | null;
};

export const serialize: Serializer<"promoCode"> = {
  storage: "local",
  load: (storedValue: PromoCodeState) => {
    if (storedValue) {
      updateSessionState("browser", {
        promoCodePersisted: storedValue.promoCodeApplied?.length > 0,
        promoCodePersistedApplied: storedValue.promoCodeApplied,
      });
    }
    return storedValue;
  },
  save: (promoCode: PromoCodeState) => ({
    ...promoCode,
    promoCodeError: "",
  }),
};

const promoCode = createSlice({
  name: "promoCode",
  initialState: promoCodeInitialState,
  reducers: {
    setPromoCode(state, action: PayloadAction<Partial<PromoCodeState>>) {
      return {
        ...state,
        ...action.payload,
      };
    },
    clearPromoCode() {
      return {
        ...promoCodeInitialState,
      };
    },
  },
});

export function getPromoCodeState(
  state: CoreReduxState = getStore().getState()
) {
  const { recommendedPlan, promoCode: promoCodeSlice } = state;
  if (
    recommendedPlan?.billing_cycle_in_months === 1 &&
    !promoCodeSlice.promoCodeIsVip &&
    isPlanSelectionEligible(state)
  ) {
    return promoCodeInitialState;
  }
  return promoCodeSlice;
}

/**
 * Checks if the user had a promo code persisted from a prior session.
 */
export function isPromoCodePersisted() {
  return getSessionState("browser").promoCodePersisted;
}

/**
 * Used for tracking if we currently apply a promo code from a prior session.
 */
export function isPromoCodeFromPriorSession(promoCodeApplied: string) {
  return (
    promoCodeApplied === getSessionState("browser").promoCodePersistedApplied
  );
}

export function resetPromoCodeAppliedFromPriorSession() {
  updateSessionState("browser", {
    promoCodePersistedApplied: null,
  });
}

export const { setPromoCode, clearPromoCode } = promoCode.actions;

export default promoCode;
